import {
  ADDITIONAL_FACILITY_INDEXES,
  ADMISSION_POSSIBLE,
  MAX_REHAB_FACILITIES,
  PREDICAMENT_STATE_NO,
  PREDICAMENT_STATE_YES,
  REHAB_FORM_NAMES,
} from "core/consts";
import { Auction, GKVGeneralFormData } from "core/types";
import {
  Button,
  ControlledDatePicker,
  ControlledInput,
  ControlledRadioGroup,
} from "ds/ui";
import { parseAddress } from "dsl/ecosystems/PatientForms/RehabForms/GKVGeneralForm";
import { DownloadIcon, PlusIcon, TrashIcon } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { useTranslations } from "translations";
import { FormFieldConfigProps } from "..";
import { SourceInformation } from "../../useDocsForm";
import { SourceWrapper } from "../../utils";

type PreferedClinicNamePrefix =
  | "prefered_clinic_"
  | `prefered_clinic_${(typeof ADDITIONAL_FACILITY_INDEXES)[number]}_`;

export const PatientStreetField = ({
  skipRequiredValidation,
  translations,
}: FormFieldConfigProps<typeof REHAB_FORM_NAMES.GKV_GENERAL>) => {
  const postfachValue = useWatch({ name: "patient_postfach" });
  const postfachFilled =
    typeof postfachValue === "string" && postfachValue.trim().length > 0;

  return (
    <ControlledInput
      isRequired={!skipRequiredValidation && !postfachFilled}
      label={translations.address.street}
      name="patient_street"
      placeholder=" "
    />
  );
};

export const PatientPostfachField = ({
  skipRequiredValidation,
  translations,
}: FormFieldConfigProps<typeof REHAB_FORM_NAMES.GKV_GENERAL>) => {
  const { setValue } = useFormContext<GKVGeneralFormData>();
  const streetValue = useWatch({ name: "patient_street" });
  const streetFilled =
    typeof streetValue === "string" && streetValue.trim().length > 0;

  return (
    <ControlledInput
      isRequired={!skipRequiredValidation && !streetFilled}
      label={translations.patientForms.generalForm.insuredPerson.postfach}
      name="patient_postfach"
      placeholder=" "
      onChange={() => {
        setValue("patient_housenumber", "");
      }}
    />
  );
};

export const CommunicationInGermanPossibleField = ({
  ariaLabelledById,
  formName,
  skipRequiredValidation,
  sourceInformation,
  translations,
}: FormFieldConfigProps<typeof REHAB_FORM_NAMES.GKV_GENERAL>) => {
  const { setValue } = useFormContext<GKVGeneralFormData>();
  const communicationInGermanPossible = useWatch({
    name: "communication_in_german_possible",
  });

  const options = [
    {
      label: translations.actions.yes,
      value: PREDICAMENT_STATE_YES,
    },
    {
      label: translations.actions.no,
      subForm: () => (
        <SourceWrapper<typeof REHAB_FORM_NAMES.GKV_GENERAL>
          fieldName="communication_other_language"
          formName={formName}
          isIconOnly
          sourceInformation={sourceInformation}
        >
          <ControlledInput
            className="pt-4"
            isRequired={!skipRequiredValidation}
            label={
              translations.patientForms.generalForm.sectionTwo
                .communicationNoGerman
            }
            name="communication_other_language"
            placeholder=" "
          />
        </SourceWrapper>
      ),
      value: PREDICAMENT_STATE_NO,
    },
  ];

  useEffect(() => {
    if (communicationInGermanPossible === PREDICAMENT_STATE_YES) {
      setValue("communication_other_language", "");
    }
  }, [communicationInGermanPossible, setValue]);

  return (
    <ControlledRadioGroup
      aria-labelledby={ariaLabelledById}
      isRequired={!skipRequiredValidation}
      name="communication_in_german_possible"
      options={options}
      size="md"
    />
  );
};

const FacilityFields = ({
  auction,
  clearFacilityFields,
  facilityIndex,
  fieldPrefix,
  formName,
  handleProviderImport,
  isRequired,
  showImportButton,
  sourceInformation,
}: {
  fieldPrefix: PreferedClinicNamePrefix;
  isRequired: boolean;
  showImportButton?: boolean;
  auction?: Auction;
  handleProviderImport?: () => void;
  facilityIndex: number;
  formName: typeof REHAB_FORM_NAMES.GKV_GENERAL;
  sourceInformation: SourceInformation<typeof REHAB_FORM_NAMES.GKV_GENERAL>;
  clearFacilityFields?: (
    idx: (typeof ADDITIONAL_FACILITY_INDEXES)[number],
  ) => void;
}) => {
  const translations = useTranslations();

  return (
    <div
      className="flex flex-col gap-2"
      data-testid={`rehab_facility_${facilityIndex}`}
    >
      <hr className="mt-4 mb-2 border-gray-400" />
      <h4 className="text-body-md font-bold">
        {`${facilityIndex}.
        ${translations.patientForms.generalForm.sectionTwo.facility}`}
      </h4>
      {showImportButton && auction?.winner && handleProviderImport && (
        <div className="ml-4">
          <Button
            color="primary"
            id="import-provider-from-recare"
            startContent={<DownloadIcon />}
            variant="light"
            onPress={handleProviderImport}
          >
            {translations.patientForms.generalForm.importProviderInformation}
          </Button>
        </div>
      )}
      <div className="flex flex-col">
        <SourceWrapper<typeof REHAB_FORM_NAMES.GKV_GENERAL>
          fieldName={`${fieldPrefix}name`}
          formName={formName}
          isIconOnly
          sourceInformation={sourceInformation}
        >
          <ControlledInput
            isRequired={isRequired}
            label={translations.institution.name}
            name={`${fieldPrefix}name`}
            placeholder=" "
          />
        </SourceWrapper>
        <SourceWrapper<typeof REHAB_FORM_NAMES.GKV_GENERAL>
          fieldName={`${fieldPrefix}street`}
          formName={formName}
          isIconOnly
          sourceInformation={sourceInformation}
        >
          <ControlledInput
            isRequired={isRequired}
            label={translations.address.street}
            name={`${fieldPrefix}street`}
            placeholder=" "
          />
        </SourceWrapper>
        <SourceWrapper<typeof REHAB_FORM_NAMES.GKV_GENERAL>
          fieldName={`${fieldPrefix}house_number`}
          formName={formName}
          isIconOnly
          sourceInformation={sourceInformation}
        >
          <ControlledInput
            isRequired={isRequired}
            label={translations.address.number}
            name={`${fieldPrefix}house_number`}
            placeholder=" "
          />
        </SourceWrapper>
        <SourceWrapper<typeof REHAB_FORM_NAMES.GKV_GENERAL>
          fieldName={`${fieldPrefix}postal_code`}
          formName={formName}
          isIconOnly
          sourceInformation={sourceInformation}
        >
          <ControlledInput
            isRequired={isRequired}
            label={translations.address.zipcode}
            name={`${fieldPrefix}postal_code`}
            placeholder=" "
          />
        </SourceWrapper>
        <SourceWrapper<typeof REHAB_FORM_NAMES.GKV_GENERAL>
          fieldName={`${fieldPrefix}city`}
          formName={formName}
          isIconOnly
          sourceInformation={sourceInformation}
        >
          <ControlledInput
            isRequired={isRequired}
            label={translations.address.city}
            name={`${fieldPrefix}city`}
            placeholder=" "
          />
        </SourceWrapper>
        <SourceWrapper<typeof REHAB_FORM_NAMES.GKV_GENERAL>
          fieldName={`${fieldPrefix}phone`}
          formName={formName}
          isIconOnly
          sourceInformation={sourceInformation}
        >
          <ControlledInput
            isRequired={isRequired}
            label={translations.contactInformation.phoneNumber}
            name={`${fieldPrefix}phone`}
            placeholder=" "
          />
        </SourceWrapper>
        <SourceWrapper<typeof REHAB_FORM_NAMES.GKV_GENERAL>
          fieldName={`${fieldPrefix}admission_possible`}
          formName={formName}
          isIconOnly
          sourceInformation={sourceInformation}
        >
          <ControlledRadioGroup
            isRequired={isRequired}
            label={
              translations.patientForms.generalForm.sectionTwo.admissionPossible
            }
            name={`${fieldPrefix}admission_possible`}
            options={[
              {
                label:
                  translations.patientForms.generalForm.sectionTwo
                    .admissionPossibleYes,
                value: ADMISSION_POSSIBLE.YES,
              },
              {
                label:
                  translations.patientForms.generalForm.sectionTwo
                    .admissionPossibleNo,
                value: ADMISSION_POSSIBLE.NO,
              },
              {
                label:
                  translations.patientForms.generalForm.sectionTwo
                    .admissionPossibleNoAnswer,
                value: ADMISSION_POSSIBLE.NO_ANSWER,
              },
            ]}
            size="md"
          />
        </SourceWrapper>
        <SourceWrapper<typeof REHAB_FORM_NAMES.GKV_GENERAL>
          fieldName={`${fieldPrefix}contacted_start_date`}
          formName={formName}
          isIconOnly
          sourceInformation={sourceInformation}
        >
          <ControlledDatePicker
            isRequired={isRequired}
            label={
              translations.patientForms.generalForm.sectionTwo.rehabIntakeDate
            }
            name={`${fieldPrefix}contacted_start_date`}
          />
        </SourceWrapper>
      </div>
      {ADDITIONAL_FACILITY_INDEXES.includes(
        facilityIndex as (typeof ADDITIONAL_FACILITY_INDEXES)[number],
      ) && (
        <div className="ml-4">
          <Button
            color="danger"
            id="remove-facility"
            startContent={<TrashIcon />}
            variant="light"
            onPress={() =>
              clearFacilityFields?.(
                facilityIndex as (typeof ADDITIONAL_FACILITY_INDEXES)[number],
              )
            }
          >
            {translations.patientForms.generalForm.sectionTwo.removeFacility}
          </Button>
        </div>
      )}
    </div>
  );
};

export const PreferredClinicContactedField = ({
  ariaLabelledById,
  auction,
  formName,
  skipRequiredValidation,
  sourceInformation,
  translations,
}: FormFieldConfigProps<typeof REHAB_FORM_NAMES.GKV_GENERAL>) => {
  const { getValues, setValue } = useFormContext<GKVGeneralFormData>();
  const initialCount = useMemo(
    () => getValues("prefered_clinic_count"),
    [getValues],
  );

  const preferedClinicContacted = useWatch({
    name: "prefered_clinic_contacted",
  });

  const [facilityCount, setFacilityCount] = useState(() => initialCount ?? 1);
  const initializedRef = useRef(false);

  useEffect(() => {
    if (initializedRef.current) {
      setValue("prefered_clinic_count", facilityCount, { shouldDirty: true });
    }
  }, [facilityCount, setValue]);

  useEffect(() => {
    initializedRef.current = true;
  }, []);

  const additionalIndexes = ADDITIONAL_FACILITY_INDEXES.filter(
    (idx) => idx <= facilityCount,
  );

  const handleProviderImport = () => {
    if (!auction?.winner?.careprovider) return;

    const { careprovider } = auction.winner;
    const { address, central_phone_number, name } = careprovider;

    const { houseNumber, street } = address?.street
      ? { houseNumber: address.street_number, street: address.street }
      : parseAddress(address?.address ?? null);

    setValue("prefered_clinic_name", name ?? null);
    setValue("prefered_clinic_street", street ?? null);
    setValue("prefered_clinic_house_number", houseNumber ?? null);
    setValue("prefered_clinic_postal_code", address?.zip_code ?? null);
    setValue("prefered_clinic_city", address?.city ?? null);
    setValue("prefered_clinic_phone", central_phone_number ?? null);
    setValue(
      "prefered_clinic_contacted_start_date",
      auction.start_date ?? null,
    );
  };

  const clearFacilityFields = (
    idx: (typeof ADDITIONAL_FACILITY_INDEXES)[number],
  ) => {
    const stringFields = [
      "name",
      "street",
      "house_number",
      "postal_code",
      "city",
      "phone",
    ] as const;

    const nullFields = ["admission_possible", "contacted_start_date"] as const;

    const fields = [...stringFields, ...nullFields] as const;

    for (let i = idx; i < facilityCount; i++) {
      for (const field of fields) {
        setValue(
          `prefered_clinic_${i}_${field}` as keyof GKVGeneralFormData,
          getValues(
            `prefered_clinic_${i + 1}_${field}` as keyof GKVGeneralFormData,
          ),
        );
      }
    }

    for (const field of stringFields) {
      setValue(
        `prefered_clinic_${facilityCount}_${field}` as keyof GKVGeneralFormData,
        "",
      );
    }
    for (const field of nullFields) {
      setValue(
        `prefered_clinic_${facilityCount}_${field}` as keyof GKVGeneralFormData,
        null,
      );
    }

    setFacilityCount((prev) => prev - 1);
  };

  const options = [
    {
      label: translations.actions.no,
      value: PREDICAMENT_STATE_NO,
    },
    {
      label: translations.actions.yes,
      value: PREDICAMENT_STATE_YES,
    },
  ];

  useEffect(() => {
    if (preferedClinicContacted === PREDICAMENT_STATE_NO) {
      setValue("prefered_clinic_name", "");
      setValue("prefered_clinic_street", "");
      setValue("prefered_clinic_house_number", "");
      setValue("prefered_clinic_postal_code", "");
      setValue("prefered_clinic_city", "");
      setValue("prefered_clinic_phone", "");
      setValue("prefered_clinic_contacted_start_date", null);
      setValue("prefered_clinic_admission_possible", null);
      for (const idx of ADDITIONAL_FACILITY_INDEXES) {
        setValue(`prefered_clinic_${idx}_name` as keyof GKVGeneralFormData, "");
        setValue(
          `prefered_clinic_${idx}_street` as keyof GKVGeneralFormData,
          "",
        );
        setValue(
          `prefered_clinic_${idx}_house_number` as keyof GKVGeneralFormData,
          "",
        );
        setValue(
          `prefered_clinic_${idx}_postal_code` as keyof GKVGeneralFormData,
          "",
        );
        setValue(`prefered_clinic_${idx}_city` as keyof GKVGeneralFormData, "");
        setValue(
          `prefered_clinic_${idx}_phone` as keyof GKVGeneralFormData,
          "",
        );
        setValue(
          `prefered_clinic_${idx}_contacted_start_date` as keyof GKVGeneralFormData,
          null,
        );
        setValue(
          `prefered_clinic_${idx}_admission_possible` as keyof GKVGeneralFormData,
          null,
        );
      }
      setFacilityCount(1);
    }
  }, [preferedClinicContacted, setValue]);

  return (
    <>
      <ControlledRadioGroup
        aria-labelledby={ariaLabelledById}
        isRequired={!skipRequiredValidation}
        name="prefered_clinic_contacted"
        options={options}
        size="md"
      />
      {preferedClinicContacted === PREDICAMENT_STATE_YES && (
        <div>
          <FacilityFields
            auction={auction}
            facilityIndex={1}
            fieldPrefix="prefered_clinic_"
            formName={formName}
            handleProviderImport={handleProviderImport}
            isRequired={!skipRequiredValidation}
            showImportButton
            sourceInformation={sourceInformation}
          />
          {additionalIndexes.map((idx) => (
            <FacilityFields
              key={idx}
              clearFacilityFields={clearFacilityFields}
              facilityIndex={idx}
              fieldPrefix={`prefered_clinic_${idx}_`}
              formName={formName}
              handleProviderImport={handleProviderImport}
              isRequired={!skipRequiredValidation}
              sourceInformation={sourceInformation}
            />
          ))}
          <hr className="my-4 border-gray-400" />
          {facilityCount < MAX_REHAB_FACILITIES && (
            <div>
              <Button
                className="w-full"
                color="primary"
                id="add-another-facility"
                startContent={<PlusIcon />}
                variant="ghost"
                onPress={() => setFacilityCount((prev) => prev + 1)}
              >
                {
                  translations.patientForms.generalForm.sectionTwo
                    .addAnotherFacility
                }
              </Button>
            </div>
          )}
        </div>
      )}
    </>
  );
};
